import React, { useMemo } from 'react';

import { LoadingIconButton, Table } from '@components/Common';
import Order from '@components/Wallet/Order';
import { TrashIcon } from '@heroicons/react/24/solid';
import { useLg } from '@hooks/responsive/useLg';
import { useDeleteAll } from '@hooks/useDeleteAll';
import { useText } from '@hooks/useText';
import { IOrder } from '@interfaces/order';
import { Typography } from '@material-tailwind/react';
import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';

import { roundAmount } from '../../utils/amount';
import { renderOrderType } from '../../utils/renderers';

type PropsType = {
  orders: IOrder[];
  onDelete: (orders: IOrder[]) => void;
  isDeleting?: boolean;
  ordersIdsToDelete?: string[];
};

const columnHelper = createColumnHelper<IOrder>();

const OrdersTable: React.FC<PropsType> = ({ orders, ordersIdsToDelete, onDelete, isDeleting }) => {
  const { t } = useText();
  const isLg = useLg();

  const { deleteAll, isAllDeleting } = useDeleteAll(() => {
    onDelete(orders);
  });

  const columns = useMemo(() => {
    const actions = columnHelper.display({
      id: 'actions',
      header: () => (
        <LoadingIconButton
          variant="text"
          color="blue"
          tooltip={t('order.cancelAll')}
          isLoading={isAllDeleting}
          onClick={deleteAll}
        >
          <TrashIcon className="text-white h-[18px]" />
        </LoadingIconButton>
      ),
      cell: (info) => {
        const isLoading =
          !isAllDeleting && isDeleting && ordersIdsToDelete?.includes(info.row.original.id);

        return (
          <LoadingIconButton
            variant="text"
            color="blue"
            tooltip={t('order.cancel')}
            isLoading={isLoading}
            onClick={() => onDelete([info.row.original])}
          >
            <TrashIcon className="text-white h-[18px]" />
          </LoadingIconButton>
        );
      },
      size: isLg ? 50 : 30,
    });

    if (!isLg) {
      return [
        columnHelper.display({
          id: 'order',
          header: () => t('order.order'),
          cell: (info) => <Order order={info.row.original} />,
        }),
        actions,
      ];
    }

    return [
      columnHelper.accessor('symbol', {
        header: () => t('order.pair'),
        cell: (info) => (
          <Typography variant="small" className="font-semibold">
            {info.getValue()}
          </Typography>
        ),
        size: 100,
      }),
      columnHelper.display({
        id: 'type',
        header: () => t('order.type'),
        cell: (info) => renderOrderType(info.row.original),
        size: 180,
      }),
      columnHelper.accessor('price', {
        header: () => t('order.price'),
        cell: (info) => (
          <Typography variant="small" className="font-semibold">
            {roundAmount(info.getValue(), 5)}
          </Typography>
        ),
        size: 100,
      }),
      columnHelper.accessor('quantity', {
        header: () => t('order.quantity'),
        cell: (info) => (
          <Typography variant="small" className="font-semibold">
            {roundAmount(info.getValue(), 5)}
          </Typography>
        ),
        size: 100,
      }),
      columnHelper.accessor('createdAt', {
        header: () => t('common.date'),
        cell: (info) => (
          <Typography variant="small" className="font-semibold whitespace-nowrap">
            {format(new Date(info.getValue()), 'MM/dd/yyyy - H:mm')}
          </Typography>
        ),
        size: 150,
      }),
      actions,
    ];
  }, [isAllDeleting, ordersIdsToDelete, isDeleting, isLg, orders]);

  return <Table columns={columns} data={orders} emptyText={t('order.empty')} />;
};

export default React.memo(OrdersTable);
